import { useState, useEffect } from 'react';
import { Check, Zap, CreditCard, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../hooks/useAuth';
import { PLAN_DETAILS, type SubscriptionPlan } from '../types';
import { formatNumber, formatDate } from '../lib/utils';
import { api } from '../lib/api';

// Stripe price IDs from env (or use from API)
const STRIPE_PRICES: Record<SubscriptionPlan, string> = {
  free: import.meta.env.VITE_STRIPE_PRICE_FREE || 'price_1T6GIfLpXsFqVVSVUnCMWTYV',
  starter: import.meta.env.VITE_STRIPE_PRICE_STARTER || 'price_1T6GIfLpXsFqVVSVxtfOGbn7',
  professional: import.meta.env.VITE_STRIPE_PRICE_PROFESSIONAL || 'price_1T6GIgLpXsFqVVSVRIMAWmqD',
  enterprise: import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE || 'price_1T6GIgLpXsFqVVSVDHYWyhai',
};

interface SubscriptionStatus {
  tier: string;
  status: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  current_period_end: string | null;
  verifications_used: number;
  verifications_limit: number;
}

// Mock payment history (will be replaced with real data from Stripe)
const mockPayments = [
  { id: '1', date: '2026-02-01', amount: 29, status: 'paid', plan: 'Professional' },
  { id: '2', date: '2026-01-01', amount: 29, status: 'paid', plan: 'Professional' },
  { id: '3', date: '2025-12-01', amount: 9, status: 'paid', plan: 'Starter' },
];

export function BillingPage() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for URL params (success/cancel from Stripe)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setError(null);
      // Could show a success toast here
    }
    if (params.get('canceled') === 'true') {
      // Could show a canceled message
    }
    // Clean URL params
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // Load subscription status
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const status = await api.getSubscriptionStatus();
        setSubscription(status);
      } catch (err) {
        console.error('Failed to load subscription:', err);
        // Use default values if API fails
        setSubscription({
          tier: user?.subscription_plan || 'free',
          status: 'active',
          stripe_subscription_id: null,
          stripe_customer_id: null,
          current_period_end: null,
          verifications_used: 0,
          verifications_limit: 1000,
        });
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [user]);

  const currentPlan = (subscription?.tier || user?.subscription_plan || 'free') as SubscriptionPlan;
  const planDetails = PLAN_DETAILS[currentPlan];
  const usage = subscription ? {
    used: subscription.verifications_used,
    limit: subscription.verifications_limit,
    percentage: subscription.verifications_limit > 0 
      ? (subscription.verifications_used / subscription.verifications_limit) * 100 
      : 0,
  } : { used: 0, limit: 1000, percentage: 0 };

  const handleUpgrade = async (plan: SubscriptionPlan) => {
    if (plan === 'free') return;
    
    setError(null);
    setCheckoutLoading(plan);
    
    try {
      const priceId = STRIPE_PRICES[plan];
      const { checkout_url } = await api.createCheckoutSession(priceId);
      
      // Redirect to Stripe Checkout
      window.location.href = checkout_url;
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setError(null);
    setPortalLoading(true);
    
    try {
      const { portal_url } = await api.createPortalSession();
      window.location.href = portal_url;
    } catch (err: any) {
      console.error('Portal error:', err);
      setError(err.response?.data?.detail || 'Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Billing & Subscription</h1>
        <p className="text-muted-foreground">
          Manage your subscription and payments
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      {/* Current plan & usage */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>Your active subscription</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold">{planDetails.name}</h3>
                <p className="text-muted-foreground">
                  ${planDetails.price}/month
                </p>
              </div>
              <Badge variant={subscription?.status === 'active' ? 'success' : 'secondary'}>
                {subscription?.status === 'active' ? 'Active' : subscription?.status || 'Active'}
              </Badge>
            </div>
            {subscription?.current_period_end && (
              <p className="text-sm text-muted-foreground">
                Renews on {formatDate(subscription.current_period_end)}
              </p>
            )}
            <Separator />
            <ul className="space-y-2">
              {planDetails.features.slice(0, 4).map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-success" />
                  {feature}
                </li>
              ))}
            </ul>
          </CardContent>
          <CardFooter className="flex gap-2">
            {subscription?.stripe_customer_id && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleManageSubscription}
                disabled={portalLoading}
              >
                {portalLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                Manage Subscription
              </Button>
            )}
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Usage</CardTitle>
            <CardDescription>Verifications this month</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">
                  {formatNumber(usage.used)} / {usage.limit === -1 ? '∞' : formatNumber(usage.limit)} verifications
                </span>
                <span className="text-sm font-medium">
                  {usage.limit === -1 ? '∞' : `${usage.percentage.toFixed(1)}%`}
                </span>
              </div>
              <Progress value={usage.limit === -1 ? 0 : usage.percentage} className="h-3" />
            </div>
            {usage.percentage > 80 && usage.limit !== -1 && (
              <div className="flex items-center gap-2 p-3 text-sm bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200 rounded-md">
                <Zap className="h-4 w-4" />
                You've used over 80% of your limit. Consider upgrading.
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Limit resets at the start of each billing period
            </p>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full" 
              onClick={() => handleUpgrade('professional')}
              disabled={currentPlan === 'professional' || currentPlan === 'enterprise' || !!checkoutLoading}
            >
              {checkoutLoading === 'professional' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Zap className="mr-2 h-4 w-4" />
              )}
              {currentPlan === 'professional' || currentPlan === 'enterprise' ? 'Current Plan' : 'Upgrade Plan'}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Plans comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Compare Plans</CardTitle>
          <CardDescription>Choose the best plan for your needs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {(Object.entries(PLAN_DETAILS) as [SubscriptionPlan, typeof PLAN_DETAILS['free']][]).map(([key, plan]) => (
              <Card key={key} className={`relative ${key === currentPlan ? 'border-primary border-2' : ''}`}>
                {key === currentPlan && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2">
                    Current
                  </Badge>
                )}
                {key === 'professional' && key !== currentPlan && (
                  <Badge variant="secondary" className="absolute -top-2 left-1/2 -translate-x-1/2">
                    Popular
                  </Badge>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">${plan.price}</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-success mt-0.5 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    variant={key === currentPlan ? 'outline' : 'default'}
                    disabled={key === currentPlan || key === 'free' || !!checkoutLoading}
                    onClick={() => handleUpgrade(key)}
                  >
                    {checkoutLoading === key ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {key === currentPlan ? 'Current' : key === 'free' ? 'Free' : 'Select'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Manage Subscription Button */}
      {subscription?.stripe_customer_id && (
        <Card>
          <CardHeader>
            <CardTitle>Manage Subscription</CardTitle>
            <CardDescription>
              Update payment method, view invoices, or cancel subscription
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="w-full md:w-auto"
            >
              {portalLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
              )}
              Open Billing Portal
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Payment history */}
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>Your previous payments</CardDescription>
        </CardHeader>
        <CardContent>
          {subscription?.stripe_customer_id ? (
            <p className="text-sm text-muted-foreground mb-4">
              View detailed payment history in the{' '}
              <button 
                onClick={handleManageSubscription}
                className="text-primary hover:underline"
              >
                Billing Portal
              </button>
            </p>
          ) : null}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockPayments.length > 0 ? (
                mockPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.date)}</TableCell>
                    <TableCell>{payment.plan}</TableCell>
                    <TableCell>${payment.amount}</TableCell>
                    <TableCell>
                      <Badge variant="success">Paid</Badge>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No payment history yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
